import React, {ReactNode} from 'react';
import {useSelector} from 'react-redux';
import {t} from '@lingui/core/macro';

import {MonitorStatusBadge} from 'entities/monitor';
import {userSelector} from 'entities/user';
import RowTags from 'shared/ui/Table/RowTags/RowTags';
import IPortMonitor from 'features/port/model/PortMonitor/IPortMonitor';
import {getFormattedDate} from 'shared/lib/datetime';
import {isTranslate} from 'shared/lib/lang';
import {
    getPortDesiredStateLabel,
    getPortMonitorAddress,
    getPortMonitorDateFormat,
} from 'features/port/lib/portMonitorHelper';

import PortMonitorStateBadge from '../../PortMonitorStateBadge/PortMonitorStateBadge';

import styles from './PortMonitorSummary.module.scss';

interface IPortMonitorSummaryProps {
    portMonitor: IPortMonitor,
}

const PortMonitorSummary: React.FC<IPortMonitorSummaryProps> = ({portMonitor}) => {
    const user = useSelector(userSelector);

    const dateOptions = {
        format: getPortMonitorDateFormat(isTranslate()),
        tzString: user?.timezone,
        nullValue: '—',
    };

    const tags = portMonitor.tags ?? [];

    const items: {label: string, value: ReactNode}[] = [
        {label: t`Адрес`, value: getPortMonitorAddress(portMonitor.ip, portMonitor.port)},
        {label: t`Желаемое состояние`, value: getPortDesiredStateLabel(portMonitor.desired_state)},
        {
            label: t`Текущее состояние`,
            value: <PortMonitorStateBadge status={portMonitor.status} ok={portMonitor.ok} textOnly/>,
        },
        {label: t`Интервал проверки`, value: <>{portMonitor.check_interval} {t`мин.`}</>},
        {label: t`Таймаут`, value: <>{portMonitor.timeout} {t`сек.`}</>},
        {label: t`Порог срабатывания`, value: portMonitor.threshold},
        {label: t`Последняя проверка`, value: getFormattedDate(portMonitor.last_check, dateOptions)},
        {label: t`Следующая проверка`, value: getFormattedDate(portMonitor.next_check, dateOptions)},
        {label: t`Монитор создан`, value: getFormattedDate(portMonitor.created_at, dateOptions)},
    ];

    return (
        <div className={styles.section}>
            <span className={styles.state}>
                {t`Состояние`}: <MonitorStatusBadge status={portMonitor.status}/>
            </span>

            <span className={styles.line}/>

            <div className={styles.grid}>
                {items.map(({label, value}) => (
                    <span key={label} className={styles.item}>
                        <span className={styles.label}>{label}:</span>
                        {value}
                    </span>
                ))}
                {tags.length > 0 && (
                    <span className={styles.item}>
                        <span className={styles.label}>{t`Теги`}:</span>
                        <RowTags tags={tags}/>
                    </span>
                )}
            </div>
        </div>
    );
};

export default PortMonitorSummary;
